import { Clock, User as UserIcon } from "lucide-react";

/**
 * Subtle SAP/Fiori-style audit metadata strip.
 *
 *   <AuditMeta record={lead} />
 *   <AuditMeta record={assessment} variant="inline" />
 *
 * Renders "Last updated by <Name> on <date>" using the resolution order
 * baked into the backend stamp (full name → display name → email → id).
 * Falls back to "Created by …" when no updated_by_name is present yet.
 *
 * Variants:
 *   - "block"   (default) — two-line stacked layout for detail headers
 *   - "inline"            — single muted row for list pages / cards
 *
 * Designed to be PURELY ADDITIVE — never throws if the record predates the
 * audit rollout and is missing every field. Renders nothing in that case.
 */
function fmtAuditDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // SAP-style: 23-May-2026 · 14:08
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  }).replace(/ /g, "-");
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return `${date} · ${time}`;
}

function pickName(record, kind) {
  // Backend stamps these directly; client_releases additionally stores
  // a nested `released_by` for legacy reasons — pick whichever is present.
  if (!record) return "";
  const direct = record[`${kind}_by_name`];
  if (direct) return direct;
  const nested = record[`${kind}_by`];
  if (nested && typeof nested === "object") {
    return nested.name || nested.email || nested.id || "";
  }
  return "";
}

export function AuditMeta({ record, variant = "block", testId }) {
  if (!record) return null;
  const updatedName = pickName(record, "updated") || pickName(record, "created");
  const createdName = pickName(record, "created");
  const updatedAt = record.updated_at || record.status_updated_at || record.created_at;
  const createdAt = record.created_at;
  if (!updatedName && !createdName) return null;

  if (variant === "inline") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-[#9CA3AF]"
        data-testid={testId || "audit-meta-inline"}
      >
        <Clock className="w-3 h-3" />
        <span>
          Last updated by{" "}
          <span className="text-[#6B7280] font-medium">{updatedName || "System"}</span>
          {updatedAt && (
            <>
              {" "}on{" "}
              <span className="text-[#6B7280]">{fmtAuditDate(updatedAt)}</span>
            </>
          )}
        </span>
      </span>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#9CA3AF]"
      data-testid={testId || "audit-meta-block"}
    >
      {updatedName && (
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last updated by{" "}
          <span className="text-[#6B7280] font-medium">{updatedName}</span>
          {updatedAt && (
            <span className="text-[#6B7280]"> · {fmtAuditDate(updatedAt)}</span>
          )}
        </span>
      )}
      {createdName && createdName !== updatedName && (
        <span className="inline-flex items-center gap-1">
          <UserIcon className="w-3 h-3" />
          Created by{" "}
          <span className="text-[#6B7280] font-medium">{createdName}</span>
          {createdAt && (
            <span className="text-[#6B7280]"> · {fmtAuditDate(createdAt)}</span>
          )}
        </span>
      )}
    </div>
  );
}

export default AuditMeta;
